import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const STEPS = ['validating', 'parsing', 'importing', 'completed']

function ProgressStep({ label, status, isCurrent }) {
  const getIcon = () => {
    if (status === 'completed') {
      return (
        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )
    }
    if (isCurrent) {
      return (
        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )
    }
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
        <div className="w-3 h-3 rounded-full bg-gray-400" />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      {getIcon()}
      <span className={isCurrent ? 'font-medium text-gray-900' : 'text-gray-500'}>
        {label}
      </span>
    </div>
  )
}

function FeedImportProgress({
  step,
  progress = 0,
  imported = 0,
  total = 0,
  skipped = 0,
  skippedReasons = [],
  error,
  onRetry,
}) {
  const { t } = useTranslation('ecommerce')

  const stepLabels = {
    validating: t('import.steps.validating', 'URL validieren'),
    parsing: t('import.steps.parsing', 'Feed parsen'),
    importing: t('import.steps.importing', 'Produkte importieren'),
    completed: t('import.steps.completed', 'Fertig'),
  }

  const currentIndex = STEPS.indexOf(step)

  if (step === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-red-800">
          {t('import.error.title', 'Import fehlgeschlagen')}
        </h3>
        <p className="mt-2 text-sm text-red-700">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-4 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
          >
            {t('import.error.retry', 'Erneut versuchen')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Step Indicators */}
      <div className="flex flex-col gap-4">
        {STEPS.map((s, i) => (
          <ProgressStep
            key={s}
            label={stepLabels[s]}
            status={i < currentIndex ? 'completed' : i === currentIndex ? 'current' : 'pending'}
            isCurrent={i === currentIndex}
          />
        ))}
      </div>

      {/* Progress Details */}
      {step === 'parsing' && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{t('import.parsing', 'Feed wird analysiert...')}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{t('import.progress', 'Importiere Produkte...')}</span>
            <span>{imported} / {total}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${total > 0 ? (imported / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {step === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">
              {t('import.success', '{{count}} Produkte importiert', { count: imported })}
            </span>
          </div>

          {skipped > 0 && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-sm text-yellow-700">
                {t('import.skipped', '{{count}} Produkte übersprungen', { count: skipped })}
              </p>
              {skippedReasons.length > 0 && (
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  {skippedReasons.map((reason, i) => (
                    <li key={i}>• {reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ProgressStep.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['completed', 'current', 'pending']).isRequired,
  isCurrent: PropTypes.bool.isRequired,
}

FeedImportProgress.propTypes = {
  step: PropTypes.oneOf(['validating', 'parsing', 'importing', 'completed', 'error']).isRequired,
  progress: PropTypes.number,
  imported: PropTypes.number,
  total: PropTypes.number,
  skipped: PropTypes.number,
  skippedReasons: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.string,
  onRetry: PropTypes.func,
}

export default FeedImportProgress
